import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "@tanstack/react-form"
import { Button } from "@/components/ui/button"
import { useToast } from "@/components/ui/use-toast"
import DeviceAssignment from "@/components/DeviceAssignment"
import CustomerInformationForm from "@/components/CustomerInformationForm"
import useCreateCustomer from "@/hooks/api/use-create-customer"
import AddCustomerPageHeader from "@/components/AddCustomerPageHeader"
import { useQueryClient } from "@tanstack/react-query"
import { useGetCustomerList } from "@/hooks/api/use-get-customer-list"
import { AssignDevice, CreateCustomerRequestDTO } from "@/gen/api"
import { useAuth } from "@/hooks/useAuth"
import useCurrentUser from "@/hooks/use-current-user"
import { hasPermission } from "@/lib/admin-permissions"

export default function AddCustomerPage() {
  const navigate = useNavigate()
  const { councilId } = useParams()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { hasRole } = useCurrentUser()
  const scopedCouncilId = councilId ? Number(councilId) : undefined
  const canWriteCustomers =
    hasRole("super_admin") ||
    hasPermission(user?.permissions, "customers", "write_own", scopedCouncilId) ||
    hasPermission(user?.permissions, "customers", "write_all", scopedCouncilId)

  const { mutate: createCustomer, isPending: isCreatingCustomer } = useCreateCustomer({
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Customer has been added successfully",
      })
      queryClient.invalidateQueries({
        queryKey: [useGetCustomerList.QUERY_KEY],
      })
      navigate(councilId ? `/council/${councilId}/customers` : "/customers")
    },
    onError: (message: string) => {
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      })
    },
  })

  const form = useForm({
    defaultValues: {
      name: "",
      email: "",
      address: "",
      deviceIds: [] as string[],
      devices: [] as AssignDevice[],
    },
    onSubmit: async ({ value }) => {
      const requestData: CreateCustomerRequestDTO = {
        fullName: value.name,
        email: value.email,
        address: value.address,
        ...(value.devices.length > 0 ? { devices: value.devices } : {}),
      }
      createCustomer({ councilId: councilId || "", req: requestData })
    },
  })

  if (!canWriteCustomers) {
    return (
      <div className="flex min-h-[calc(100vh-12rem)] items-center justify-center">
        <div className="max-w-md rounded-lg border border-dashed p-8 text-center">
          <h1 className="text-xl font-semibold">Access denied</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            You need customer write permission to add customers.
          </p>
          <Button className="mt-4" variant="outline" onClick={() => navigate(-1)}>
            Go back
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <AddCustomerPageHeader />

      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-6"
      >
        <CustomerInformationForm form={form} />
        <DeviceAssignment councilId={councilId || ""} form={form} />

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
            disabled={isCreatingCustomer}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isCreatingCustomer}>
            {isCreatingCustomer ? "Creating..." : "Create Customer"}
          </Button>
        </div>
      </form>
    </div>
  )
}
